import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { Home, CreditCard, TrendingUp, Users, Calendar, Globe, ChevronDown, FileDown, History, Eye, Loader2, RefreshCw } from 'lucide-react';
import { API_BASE } from '../config';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../i18n/LanguageContext';

// Taux de conversion depuis FCFA (base)
// Les prix sont stockés en FCFA dans la base de données
const EXCHANGE_RATES = {
  FCFA: 1,
  EUR: 1 / 655,      // 1 EUR = 655 FCFA
  USD: 1 / 600,      // 1 USD ≈ 600 FCFA (approximatif)
  GBP: 1 / 760,      // 1 GBP ≈ 760 FCFA (approximatif)
  NGN: 1 / 0.45,     // 1 NGN ≈ 0.45 FCFA (1 FCFA ≈ 2.22 NGN)
  JPY: 1 / 4,        // 1 JPY ≈ 4 FCFA (approximatif)
};

// Convertir FCFA vers une autre devise
const convertFCFAToCurrency = (fcfa, targetCurrency) => {
  if (!fcfa || typeof fcfa !== 'number') return 0;
  const rate = EXCHANGE_RATES[targetCurrency] || 1;
  return fcfa * rate;
};

// Formater le prix selon la devise
const formatPrice = (fcfa, currency) => {
  if (!fcfa || typeof fcfa !== 'number') return '0.00';
  
  const convertedAmount = convertFCFAToCurrency(fcfa, currency);
  
  if (currency === 'FCFA') {
    return convertedAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } else if (currency === 'JPY' || currency === 'NGN') {
    return convertedAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } else {
    return convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

// Obtenir le symbole de la devise
const getCurrencySymbol = (currency) => {
  const symbols = {
    FCFA: 'FCFA',
    EUR: 'EUR',
    USD: 'USD',
    GBP: 'GBP',
    NGN: 'NGN',
    JPY: 'JPY'
  };
  return symbols[currency] || 'FCFA';
};

// Noms complets des devises (reserved for display)
// eslint-disable-next-line no-unused-vars
const _CURRENCY_NAMES = {
  FCFA: 'Franc CFA',
  EUR: 'Euro',
  USD: 'Dollar US',
  GBP: 'Livre Sterling',
  NGN: 'Naira Nigérian',
  JPY: 'Yen Japonais'
};

const CURRENCY_OPTIONS = [
  { value: 'FCFA', label: 'Franc CFA (FCFA)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'Dollar US (USD)' },
  { value: 'GBP', label: 'Livre Sterling (GBP)' },
  { value: 'NGN', label: 'Naira Nigérian (NGN)' },
  { value: 'JPY', label: 'Yen Japonais (JPY)' }
];

const getFeatureDisplay = (featureKey) => {
  const featureMap = {
    'pointage': 'Attendance',
    'historique': 'History',
    'calendrier': 'Calendar',
    'planning': 'Planning',
    'communiques': 'Announcements',
    'dashboard': 'Dashboard',
    'employees': 'Employees',
    'reports': 'Reports',
    'badges': 'Digital Badges',
    'formations': 'Training Courses',
    'notes_frais': 'Expense Reports',
    'conges': 'Holidays',
    'soldes': 'Balance Management',
    'documents': 'Documents',
    'profil': 'Profile',
    'audit_photos': 'Audit Photos',
    'ideas': 'Ideas Box',
    'performance': 'Performance',
    'billing': 'Billing',
    'maps': 'Maps',
    'gps': 'GPS',
    'help_faq': 'Help & FAQ'
  };
  return featureMap[featureKey] || featureKey;
};

const MOBILE_BREAKPOINT = 768;

// Real-time days remaining from trial end date (date-only to avoid timezone issues)
const getDaysRemaining = (trialEndsAt) => {
  if (!trialEndsAt) return 0;
  const end = new Date(trialEndsAt);
  const now = new Date();
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(0, Math.floor((endUTC - todayUTC) / (24 * 60 * 60 * 1000)));
};

const BillingDashboard = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const { billingData: contextBillingData, billingLoaded, onRefetchBilling } = useOutletContext() || {};
  const [localBillingData, setLocalBillingData] = useState(null);
  const [loading, setLoading] = useState(!contextBillingData && !billingLoaded);
  const [error, setError] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('billing_currency') || 'FCFA';
  });
  const billingData = contextBillingData || localBillingData;
  const status = (billingData?.status || '').toString().toLowerCase().replace(/\s+/g, '_');
  const isTrialActive = status === 'trial' || status === 'free_trial';
  const isPaidPlan = status === 'active';
  const isCancelled = status === 'cancelled' || status === 'canceled';
  const isSuspended = status === 'suspended' || status === 'past_due' || status === 'unpaid';
  const isCancellationScheduled = !!billingData?.cancelAtPeriodEnd;
  const prettyStatus = (s) => {
    if (!s) return '';
    const norm = String(s).toLowerCase().replace(/[_-]+/g, ' ').trim();
    return norm.charAt(0).toUpperCase() + norm.slice(1);
  };
  const daysRemaining = useMemo(() => {
    if (isTrialActive && billingData?.trialEndsAt) {
      return getDaysRemaining(billingData.trialEndsAt);
    }
    return billingData?.daysRemaining ?? 0;
  }, [isTrialActive, billingData?.trialEndsAt, billingData?.daysRemaining]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [currencyDropdownRect, setCurrencyDropdownRect] = useState(null);
  const currencyDropdownRef = useRef(null);
  const currencyPortalRef = useRef(null);
  const currencyTriggerRef = useRef(null);
  // Payment history
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [filterFrom, setFilterFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return d.toISOString().split('T')[0];
  });
  const [filterTo, setFilterTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailMessage, setTestEmailMessage] = useState('');
  const [sendingTestRenewal, setSendingTestRenewal] = useState(false);
  const [testRenewalMessage, setTestRenewalMessage] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = currencyDropdownRef.current?.contains(e.target);
      const inPortal = currencyPortalRef.current?.contains(e.target);
      if (!inTrigger && !inPortal) {
        setCurrencyDropdownOpen(false);
        setCurrencyDropdownRect(null);
      }
    };
    if (currencyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [currencyDropdownOpen]);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);


  useEffect(() => {
    if (contextBillingData) {
      setLoading(false);
      setLocalBillingData(null);
      return;
    }
    if (billingLoaded) {
      setLoading(false);
      if (!contextBillingData) {
        fetchBillingStatus();
      }
      return;
    }
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextBillingData, billingLoaded]);

  // Refetch on focus (e.g. returning from Plans page after selecting a plan)
  useEffect(() => {
    const onFocus = () => {
      if (onRefetchBilling) onRefetchBilling();
      else fetchBillingStatus();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefetchBilling]);

  // Real-time: refetch billing every 60s on this page so "jours restants" stays current
  useEffect(() => {
    if (!onRefetchBilling && !contextBillingData) return;
    const interval = setInterval(() => {
      if (onRefetchBilling) onRefetchBilling();
      else fetchBillingStatus();
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefetchBilling, contextBillingData]);

  // Fetch payment history (invoices) when filter or billing data is available
  const fetchInvoices = async () => {
    try {
      setInvoicesError('');
      setInvoicesLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setInvoices([]);
        return;
      }
      const params = new URLSearchParams();
      if (filterFrom) params.set('from', filterFrom);
      if (filterTo) params.set('to', filterTo);
      const url = `${API_BASE}/api/billing/invoices${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      }, 15000);
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error('❌ Erreur invoices:', err);
      setInvoicesError(t('billing.error_invoices'));
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Fetch invoices only when paid plan and when filter changes — avoid refetch on every billingData reference change (fixes repeated loading)
  useEffect(() => {
    if (!isPaidPlan) return;
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaidPlan, filterFrom, filterTo]);

  // Auto-refresh payment history every 60s so new data appears without clicking Refresh
  useEffect(() => {
    if (!isPaidPlan) return;
    const interval = setInterval(() => fetchInvoices(), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaidPlan, filterFrom, filterTo]);

  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const handleDownloadPdf = async (invoiceId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setPdfLoadingId(invoiceId);
    try {
      // Stream PDF via our backend so the browser never hits Stripe S3 (avoids blank/DNS issues)
      const url = `${API_BASE}/api/billing/invoices/${encodeURIComponent(invoiceId)}/pdf-stream`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'PDF unavailable');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('❌ Download PDF:', err);
    } finally {
      setPdfLoadingId(null);
    }
  };

  const fetchBillingStatus = async () => {
    if (contextBillingData) return;
    try {
      setError('');
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Vous devez être connecté pour voir les informations de facturation');
      }
      const response = await fetchWithTimeout(
        `${API_BASE}/api/billing/status`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        },
        15000
      );
      if (!response.ok) throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      const data = await response.json();
      setLocalBillingData(data);
    } catch (err) {
      console.error('❌ Erreur billing status:', err);
      setError(t('billing.error_load'));
      setLocalBillingData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChoosePlan = () => {
    if (onNavigate) {
      onNavigate('billing-plans');
    } else {
      window.location.href = '/facturation/plans';
    }
  };

  const handleCurrencyChange = (currency) => {
    if (!currency) return;
    setSelectedCurrency(currency);
    localStorage.setItem('billing_currency', currency);
  };

  const handleRefresh = () => {
    if (onRefetchBilling) onRefetchBilling();
    else fetchBillingStatus();
    if (isPaidPlan) fetchInvoices();
  };

  const handleCancelSubscription = async () => {
    if (!billingData) return;
    if (cancellingSubscription) return;
    if (billingData.cancelAtPeriodEnd) {
      alert(t('billing.cancellation_already_scheduled') || 'Cancellation already scheduled.');
      return;
    }
    const ok = window.confirm(t('billing.cancel_confirm') || 'Cancel subscription at period end? Auto-renewal will stop, and access stays until the end of the billing period.');
    if (!ok) return;
    try {
      setCancellingSubscription(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not logged in');
      const res = await fetch(`${API_BASE}/api/billing/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Error ${res.status}`);
      }
      alert(t('billing.cancel_success') || 'Subscription cancellation scheduled.');
      if (onRefetchBilling) onRefetchBilling();
      else fetchBillingStatus();
    } catch (e) {
      console.error('Cancel subscription failed:', e);
      alert((t('billing.cancel_error') || 'Cancel failed: ') + (e.message || 'Unknown error'));
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleSendTestRenewalEmail = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setTestEmailMessage('Please log in first.');
      return;
    }
    const url = `${API_BASE}/api/billing/send-test-email`;
    console.log('📧 [FRONTEND] Sending test email to:', url);
    setTestEmailMessage('');
    setSendingTestEmail(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTestEmailMessage('Test email sent! Check your inbox and backend terminal for 📧 logs.');
      } else {
        setTestEmailMessage(data.error || data.message || `Error ${res.status}`);
      }
    } catch (e) {
      console.error('📧 [FRONTEND] Test email request failed:', e);
      setTestEmailMessage('Request failed: ' + (e.message || 'Network error') + '. Check console – is backend on ' + API_BASE + '?');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleTestRenewalTrigger = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setTestRenewalMessage('Please log in first.');
      return;
    }
    setTestRenewalMessage('');
    setSendingTestRenewal(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/test-renewal-trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTestRenewalMessage(data.message || 'Renewal test applied. Refreshing list…');
        fetchInvoices();
      } else {
        setTestRenewalMessage(data.error || data.message || `Error ${res.status}`);
      }
    } catch (e) {
      setTestRenewalMessage('Request failed: ' + (e.message || 'Network error'));
    } finally {
      setSendingTestRenewal(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #3b82f6',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Chargement des informations de facturation...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header: not sticky on this page so it scrolls with content */}
      <div style={{
        padding: isMobile ? '16px 16px 12px' : '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('billing.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CreditCard size={32} />
            {t('billing.dashboard_title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('billing.dashboard_subtitle')}
        </p>
        {/* Langue + Devise - same row; pointer-events auto so dropdown is clickable */}
        <div style={{
          marginTop: '12px',
          marginBottom: isMobile ? '4px' : 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 1051
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>{t('billing.language')}</span>
            <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 8, color: '#fff' }} />
          </div>
          {/* Sélecteur de devise - custom dropdown; ensure clickable */}
          <div
            ref={currencyDropdownRef}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: isMobile ? '100%' : undefined,
              maxWidth: isMobile ? '100%' : undefined,
              minWidth: isMobile ? 0 : 180,
              position: 'relative',
              zIndex: 1052
            }}
          >
            <Globe size={18} style={{ color: 'rgba(255, 255, 255, 0.8)', flexShrink: 0 }} />
          <div style={{ flex: isMobile ? 1 : undefined, minWidth: isMobile ? 0 : 180, position: 'relative', width: isMobile ? '100%' : undefined }}>
            <button
              ref={currencyTriggerRef}
              type="button"
              aria-haspopup="listbox"
              aria-expanded={currencyDropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                if (!currencyDropdownOpen) {
                  const rect = currencyTriggerRef.current?.getBoundingClientRect();
                  if (rect) setCurrencyDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                }
                setCurrencyDropdownOpen(!currencyDropdownOpen);
              }}
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: '#ffffff',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                textAlign: 'left',
                boxSizing: 'border-box'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t(`billing.currency_${selectedCurrency.toLowerCase()}`) || CURRENCY_OPTIONS.find(o => o.value === selectedCurrency)?.label || selectedCurrency}
              </span>
              <ChevronDown size={16} style={{ flexShrink: 0, opacity: currencyDropdownOpen ? 0.7 : 1 }} />
            </button>
            {currencyDropdownOpen && currencyDropdownRect && createPortal(
              <div
                ref={currencyPortalRef}
                role="listbox"
                aria-label="Currency"
                style={{
                  position: 'fixed',
                  top: currencyDropdownRect.top,
                  left: currencyDropdownRect.left,
                  width: currencyDropdownRect.width,
                  maxWidth: 'calc(100vw - 32px)',
                  background: '#1a1f3a',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  zIndex: 10000,
                  maxHeight: '240px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxSizing: 'border-box'
                }}
              >
                {CURRENCY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={selectedCurrency === opt.value}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCurrencyChange(opt.value);
                      setCurrencyDropdownOpen(false);
                      setCurrencyDropdownRect(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: selectedCurrency === opt.value ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                      color: '#ffffff',
                      fontSize: '13px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      boxSizing: 'border-box'
                    }}
                  >
                    {t(`billing.currency_${opt.value.toLowerCase()}`) || opt.label}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="billing-content" style={{
        paddingTop: isMobile ? '12px' : '8px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>

      {billingData?.subscriptionExpired && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.2)',
          border: '2px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          color: '#fca5a5',
          backdropFilter: 'blur(10px)'
        }}>
          <strong>⚠️ {t('billing.subscription_expired')}.</strong> {t('billing.expired_banner')}
        </div>
      )}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '2px solid rgba(239, 68, 68, 0.4)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          color: '#fca5a5',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>⚠️ {error}</span>
          <button
            onClick={() => onRefetchBilling ? onRefetchBilling() : fetchBillingStatus()}
            style={{
              background: 'rgba(239, 68, 68, 0.3)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
            }}
          >
            🔄 {t('billing.retry')}
          </button>
        </div>
      )}

      {billingData && (
      <div
        key={`${selectedCurrency}-${language}`}
        className="billing-cards-grid"
        style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
        gap: '24px',
        minWidth: 0
      }}>
        {/* Statut de l'abonnement */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <TrendingUp size={20} />
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 700,
              margin: 0
            }}>
              {t('billing.status_title')}
            </h3>
          </div>
          <div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700,
                background: isTrialActive
                  ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                  : isPaidPlan
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : (isCancelled || isSuspended)
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#ffffff',
                width: 'fit-content'
              }}>
                {isCancelled
                  ? `🛑 ${(t('billing.subscription_cancelled') || 'Subscription cancelled')}`
                  : isSuspended
                    ? `⚠️ ${(t('billing.account_suspended') || 'Account suspended')}`
                    : isTrialActive
                      ? `🎁 ${t('billing.free_trial')}`
                      : isPaidPlan
                        ? `✅ ${t('billing.active')}`
                        : `❌ ${t('billing.inactive')}`}
              </div>
              <div>
                <h4 style={{
                  color: '#ffffff',
                  fontSize: '20px',
                  fontWeight: 700,
                  margin: '0 0 8px 0'
                }}>
                  {t('billing.plan_label')} {billingData?.planName || billingData?.plan}
                </h4>
                {!!status && !isPaidPlan && !isTrialActive && !isCancelled && !isSuspended && (
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', margin: 0 }}>
                    {prettyStatus(status)}
                  </p>
                )}
                {isTrialActive && (
                  <>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px',
                      margin: 0
                    }}>
                      <strong style={{ color: '#fbbf24' }}>{t('billing.days_remaining', { count: daysRemaining })}</strong>
                    </p>
                    {billingData?.trialStartsAt && (
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '13px',
                        margin: 0
                      }}>
                        {t('billing.trial_started_on', { date: new Date(billingData.trialStartsAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }) })}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {billingData && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                paddingTop: '20px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {(billingData.nextBilling || billingData.expiresAt) && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.8)'
                  }}>
                    <span>{t('billing.expires_on')} :</span>
                    <span style={{ color: '#ffffff', fontWeight: 600 }}>
                      {billingData.nextBilling || (billingData.expiresAt ? new Date(billingData.expiresAt).toISOString().split('T')[0] : '—')}
                    </span>
                  </div>
                )}
                {(isPaidPlan || isTrialActive) && !billingData?.subscriptionExpired && (
                  <>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      <span>{t('billing.monthly_amount') || t('billing.total_amount') || 'Amount'} :</span>
                      <span style={{ color: '#ffffff', fontWeight: 600, fontSize: '16px' }}>
                        {formatPrice(billingData.monthlyAmount, selectedCurrency)} {getCurrencySymbol(selectedCurrency)}
                      </span>
                    </div>
                    {(billingData.basePrice != null && (billingData.totalEmployees ?? billingData.employees) != null) && (
                      <div style={{
                        fontSize: '13px',
                        color: 'rgba(255, 255, 255, 0.75)',
                        padding: '10px 12px',
                        background: 'rgba(255, 255, 255, 0.06)',
                        borderRadius: '10px',
                        marginTop: '8px'
                      }}>
                        <div style={{ marginBottom: '6px' }}>
                          <strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{t('billing.base_price') || 'Base price'}</strong>
                          {' '}{Number(billingData.basePrice).toFixed(2)} EUR
                          {' × '}
                          <strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{t('billing.total_employees') || 'Total employees'}</strong>
                          {' '}{(billingData.totalEmployees ?? billingData.employees)}
                          {' = '}
                          <strong style={{ color: '#34d399' }}>
                            {(billingData.monthlyAmountEUR != null ? Number(billingData.monthlyAmountEUR).toFixed(2) : formatPrice(billingData.monthlyAmount, 'EUR'))} EUR
                          </strong>
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }}>
                          {billingData.billingCalculationNote || t('billing.admins_excluded_note') || 'Administrators are excluded from the employee count.'}
                        </p>
                      </div>
                    )}
                  </>
                )}
                {(billingData.features && billingData.features.length > 0) && (
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                      {t('billing.modules_included')}
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      minWidth: 0
                    }}>
                      {billingData.features.map((f, i) => (
                        <span
                          key={i}
                          className="billing-module-tag"
                          style={{
                            background: 'rgba(16, 185, 129, 0.2)',
                            color: '#34d399',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            maxWidth: '100%',
                            wordBreak: 'break-word'
                          }}
                        >
                          {getFeatureDisplay(f)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Prochaine facturation */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Calendar size={20} />
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 700,
              margin: 0
            }}>
              {t('billing.next_billing_title')}
            </h3>
          </div>
          <div>
            {isTrialActive ? (
              <div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  {t('billing.trial_ends')} :
                </p>
                <p style={{
                  color: '#ffffff',
                  fontSize: '24px',
                  fontWeight: 700,
                  marginBottom: '20px'
                }}>
                  {billingData?.nextBilling}
                </p>
                <div style={{
                  background: 'rgba(245, 158, 11, 0.15)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{
                    color: '#fbbf24',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px'
                  }}>
                    ⚠️ {t('billing.trial_ending_soon')}
                  </p>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    marginBottom: '16px'
                  }}>
                    {t('billing.trial_choose_plan_msg')}
                  </p>
                  <button
                    onClick={handleChoosePlan}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: '#ffffff',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      width: '100%',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                  >
                    🚀 {t('billing.choose_plan')}
                  </button>
                </div>
              </div>
            ) : isCancelled ? (
              <div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  {t('billing.subscription_cancelled') || 'Subscription cancelled'}
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '13px',
                  margin: 0
                }}>
                  {t('billing.subscription_cancelled_msg') || 'Auto-renewal is off and your subscription has ended. Choose a plan to reactivate your account.'}
                </p>
              </div>
            ) : isCancellationScheduled ? (
              <div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  {t('billing.cancels_on') || t('billing.expires_on')} :
                </p>
                <p style={{
                  color: '#ffffff',
                  fontSize: '24px',
                  fontWeight: 700,
                  marginBottom: '8px'
                }}>
                  {billingData?.currentPeriodEnd ? new Date(billingData.currentPeriodEnd).toISOString().split('T')[0] : (billingData?.nextBilling || '—')}
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginTop: '12px',
                  marginBottom: '4px'
                }}>
                  <strong style={{ color: '#fbbf24' }}>
                    {t('billing.cancellation_scheduled_title') || 'Cancellation scheduled'}
                  </strong>
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  margin: 0
                }}>
                  {t('billing.cancellation_scheduled_msg') || 'Auto-renewal has been stopped. You will keep access until the end date shown above.'}
                </p>
              </div>
            ) : (
              <div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  {t('billing.next_invoice')} :
                </p>
                <p style={{
                  color: '#ffffff',
                  fontSize: '24px',
                  fontWeight: 700,
                  marginBottom: '8px'
                }}>
                  {billingData?.nextBilling}
                </p>
                <p style={{
                  color: '#34d399',
                  fontSize: '20px',
                  fontWeight: 700,
                  margin: 0
                }}>
                  {formatPrice(billingData?.monthlyAmount, selectedCurrency)} {getCurrencySymbol(selectedCurrency)}
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  marginTop: '12px',
                  marginBottom: '4px'
                }}>
                  {t('billing.renewal_due_date')}: <strong style={{ color: '#fff' }}>{billingData?.nextBilling}</strong>
                </p>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  margin: 0
                }}>
                  {t('billing.auto_renewal_note')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Users size={20} />
            <h3 style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 700,
              margin: 0
            }}>
              {t('billing.quick_actions')}
            </h3>
          </div>
          <div className="billing-quick-actions-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
            gap: '12px',
            minWidth: 0
          }}>
            <button
              onClick={handleChoosePlan}
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#ffffff',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
            >
              📋 {t('billing.choose_plan')}
            </button>
            
            <button
              onClick={handleRefresh}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              🔄 {t('billing.refresh')}
            </button>

            {/* Send test email + Test renewal – hidden (set to true to show for testing) */}
            {false && (
              <>
                <button
                  onClick={handleSendTestRenewalEmail}
                  disabled={sendingTestEmail}
                  title="Send test email via Resend to your email"
                  style={{
                    background: 'rgba(16, 185, 129, 0.2)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    color: '#34d399',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    cursor: sendingTestEmail ? 'wait' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease'
                  }}
                >
                  📧 {sendingTestEmail ? 'Sending…' : 'Send test email'}
                </button>
                {testEmailMessage && (
                  <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    {testEmailMessage}
                  </p>
                )}
                {isPaidPlan && (
                  <button
                    type="button"
                    onClick={handleTestRenewalTrigger}
                    disabled={sendingTestRenewal}
                    style={{
                      background: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      color: '#93c5fd',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      cursor: sendingTestRenewal ? 'wait' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🔄 {sendingTestRenewal ? 'Applying…' : 'Test renewal'}
                  </button>
                )}
                {testRenewalMessage && (
                  <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    {testRenewalMessage}
                  </p>
                )}
              </>
            )}

            <button
              onClick={() => window.open('mailto:support@workbiflow.com', '_blank')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              🆘 {t('billing.support')}
            </button>
            
            {isPaidPlan && (
              <button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription || !!billingData?.cancelAtPeriodEnd}
                style={{
                  background: (cancellingSubscription || billingData?.cancelAtPeriodEnd) ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  cursor: (cancellingSubscription || billingData?.cancelAtPeriodEnd) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (cancellingSubscription || billingData?.cancelAtPeriodEnd) return;
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  if (cancellingSubscription || billingData?.cancelAtPeriodEnd) return;
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                🛑 {t('billing.cancel_subscription')}
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Payment History — only for paid plans; hide for free trial / no payment */}
      {billingData && isPaidPlan && (
        <div style={{
          marginTop: '32px',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <History size={20} />
            <div>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 700,
                margin: 0
              }}>
                {t('billing.payment_history_title')}
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '13px',
                margin: '4px 0 0 0'
              }}>
                {t('billing.payment_history_subtitle')}
              </p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
              {t('billing.filter_from')}
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                style={{
                  marginLeft: '8px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
            </label>
            <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
              {t('billing.filter_to')}
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                style={{
                  marginLeft: '8px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
            </label>
            <button
              onClick={() => fetchInvoices()}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(59, 130, 246, 0.3)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t('billing.filter_apply')}
            </button>
            <button
              onClick={() => fetchInvoices()}
              disabled={invoicesLoading}
              title={t('billing.refresh_history')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: invoicesLoading ? 'rgba(100,116,139,0.3)' : 'rgba(34, 197, 94, 0.3)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: invoicesLoading ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {invoicesLoading ? (
                <Loader2 size={16} className="billing-pdf-spinner" />
              ) : (
                <RefreshCw size={16} />
              )}
              {invoicesLoading ? t('billing.loading_invoices') : t('billing.refresh')}
            </button>
          </div>
          {invoicesError && (
            <p style={{ color: '#fca5a5', fontSize: '14px', marginBottom: '16px' }}>{invoicesError}</p>
          )}
          {invoicesLoading ? (
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>{t('billing.loading_invoices')}</p>
          ) : invoices.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>{t('billing.no_invoices')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                color: '#fff',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>{t('billing.invoice_date')}</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>{t('billing.invoice_number')}</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>{t('billing.transaction_id')}</th>
                    <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>{t('billing.amount')}</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>{t('billing.status')}</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600 }}>{t('billing.view')}</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600 }}>{t('billing.download_pdf')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <td style={{ padding: '12px 8px' }}>{inv.date}</td>
                      <td style={{ padding: '12px 8px' }}>{inv.number}</td>
                      <td style={{ padding: '12px 8px', fontFamily: 'monospace', fontSize: '12px' }}>{inv.payment_intent || '—'}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        {(inv.amount_paid / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {inv.currency}
                      </td>
                      <td style={{ padding: '12px 8px' }}>{inv.status}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setViewingInvoice(inv)}
                          title={t('billing.view_invoice_details')}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#93c5fd',
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleDownloadPdf(inv.id)}
                          disabled={!inv.invoice_pdf || pdfLoadingId === inv.id}
                          title={t('billing.download_pdf')}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(16, 185, 129, 0.2)',
                            color: '#34d399',
                            fontSize: '13px',
                            cursor: inv.invoice_pdf && pdfLoadingId !== inv.id ? 'pointer' : 'not-allowed',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {pdfLoadingId === inv.id ? (
                            <>
                              <Loader2 size={14} className="billing-pdf-spinner" />
                              Opening PDF…
                            </>
                          ) : (
                            <>
                              <FileDown size={14} />
                              PDF
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Invoice details modal (view first, then download) */}
          {viewingInvoice && createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="invoice-detail-title"
              onClick={() => setViewingInvoice(null)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'linear-gradient(180deg, rgba(30,41,59,0.98), rgba(15,23,42,0.98))',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '16px',
                  padding: '24px',
                  maxWidth: '420px',
                  width: '100%',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                }}
              >
                <h3 id="invoice-detail-title" style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '18px' }}>
                  {t('billing.invoice_details')}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t('billing.invoice_date')}</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{viewingInvoice.date}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t('billing.invoice_number')}</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{viewingInvoice.number}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t('billing.amount')}</span>
                    <span style={{ color: '#34d399', fontWeight: 600 }}>
                      {(viewingInvoice.amount_paid / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {viewingInvoice.currency}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t('billing.status')}</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{viewingInvoice.status}</span>
                  </div>
                  {(viewingInvoice.payment_intent) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'rgba(255,255,255,0.7)' }}>{t('billing.transaction_id')}</span>
                      <span style={{ color: '#93c5fd', fontFamily: 'monospace', fontSize: '12px' }}>{viewingInvoice.payment_intent}</span>
                    </div>
                  )}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: '0 0 16px 0' }}>
                  {t('billing.download_this_invoice_hint')}
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { handleDownloadPdf(viewingInvoice.id); setViewingInvoice(null); }}
                    disabled={!viewingInvoice.invoice_pdf || pdfLoadingId === viewingInvoice.id}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: viewingInvoice.invoice_pdf && pdfLoadingId !== viewingInvoice.id ? 'pointer' : 'not-allowed',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FileDown size={16} />
                    {pdfLoadingId === viewingInvoice.id ? 'Opening…' : t('billing.download_pdf')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewingInvoice(null)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.3)',
                      background: 'transparent',
                      color: '#fff',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('billing.close')}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .billing-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .billing-content {
            padding: 180px 60px 60px !important;
          }
        }
        .billing-content {
          animation: slideUp 0.4s ease-out;
        }
        .billing-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          background: #f5f7fa;
          min-height: 100vh;
        }

        .dashboard-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .dashboard-header h1 {
          font-size: 2.5rem;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .dashboard-header p {
          color: #666;
          font-size: 1.1rem;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 4rem;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-radius: 50%;
          border-top: 4px solid #3498db;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        }

        .billing-pdf-spinner {
          animation: spin 0.8s linear infinite;
          display: inline-block;
          vertical-align: middle;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-banner {
          background: #e74c3c;
          color: white;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .retry-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          color: white;
          cursor: pointer;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
          transition: transform 0.2s ease;
        }

        .card:hover {
          transform: translateY(-2px);
        }

        .card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem;
        }

        .card-header h3 {
          margin: 0;
          font-size: 1.2rem;
        }

        .card-content {
          padding: 1.5rem;
        }

        .status-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.trial {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .plan-info h4 {
          margin: 0;
          color: #2c3e50;
        }

        .trial-remaining {
          color: #e67e22;
          font-weight: 600;
          margin: 0.25rem 0 0 0;
        }

        .plan-details {
          border-top: 1px solid #eee;
          padding-top: 1rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .detail-row span:first-child {
          color: #666;
        }

        .detail-row span:last-child {
          font-weight: 600;
          color: #2c3e50;
        }

        .trial-info, .paid-info {
          text-align: center;
        }

        .next-billing {
          color: #666;
          margin-bottom: 0.5rem;
        }

        .billing-date {
          font-size: 1.2rem;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .billing-amount {
          font-size: 1.5rem;
          font-weight: bold;
          color: #27ae60;
        }

        .trial-warning {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .trial-warning p {
          margin: 0.25rem 0;
          color: #856404;
        }

        .upgrade-btn {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 1rem;
          transition: transform 0.2s ease;
        }

        .upgrade-btn:hover {
          transform: translateY(-2px);
        }

        .actions-grid {
          display: grid;
          gap: 1rem;
        }

        .action-btn {
          padding: 1rem;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn.primary {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
        }

        .action-btn.secondary {
          background: #f8f9fa;
          color: #2c3e50;
          border: 1px solid #dee2e6;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .billing-dashboard {
            padding: 1rem;
          }

          .billing-content {
            padding-left: 12px !important;
            padding-right: 12px !important;
            max-width: 100% !important;
            overflow-x: hidden;
          }

          .billing-cards-grid {
            grid-template-columns: 1fr !important;
            min-width: 0;
            overflow-x: hidden;
          }

          .billing-quick-actions-grid {
            grid-template-columns: 1fr 1fr !important;
            min-width: 0;
          }

          .billing-module-tag {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default BillingDashboard;